import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Bell, BellOff, Clock, AlertTriangle, FileText, Truck, AlertCircle,
  CheckCircle, Loader2, Eye, Settings, RefreshCw, Receipt
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { cn } from '../lib/utils';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { EmptyState } from '../components/common/EmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_ICONS = {
  retard_livraison: Clock,
  incident_declare: AlertTriangle,
  devis_expire_bientot: FileText,
  livraison_imminente: Truck,
  changement_statut: AlertCircle,
  nouveau_devis: FileText,
  facture_emise: Receipt,
};

const ALERT_COLORS = {
  high: 'border-l-red-500 bg-red-50/50',
  medium: 'border-l-amber-500 bg-amber-50/50',
  low: 'border-l-blue-500 bg-blue-50/50',
  info: 'border-l-[#1db584] bg-emerald-50/50',
};

const SEVERITY_LABELS = { high: 'Urgent', medium: 'Important', low: 'Info', info: 'Info' };
const SEVERITY_BADGE = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200',
  info: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const PREF_OPTIONS = [
  { value: 'both', label: 'Email + App' },
  { value: 'inapp', label: 'App seulement' },
  { value: 'email', label: 'Email seulement' },
  { value: 'off', label: 'Désactivé' },
];

const PREF_TYPES = [
  { key: 'retard_livraison', label: 'Retard de livraison', icon: Clock, desc: 'Quand une opération dépasse sa date prévue' },
  { key: 'incident_declare', label: 'Incident déclaré', icon: AlertTriangle, desc: 'Quand un incident est signalé' },
  { key: 'devis_expire_bientot', label: 'Devis expirant', icon: FileText, desc: 'Devis non validé depuis plus de 7 jours', financial: true },
  { key: 'livraison_imminente', label: 'Livraison imminente', icon: Truck, desc: 'Opération prévue dans les 24h' },
  { key: 'changement_statut', label: 'Changement critique', icon: AlertCircle, desc: 'Annulation ou blocage' },
  { key: 'nouveau_devis', label: 'Nouveau devis', icon: FileText, desc: 'Un nouveau devis a été émis', financial: true },
  { key: 'facture_emise', label: 'Facture émise', icon: Receipt, desc: 'Nouvelle facture disponible', financial: true },
];

const ClientAlerts = () => {
  const navigate = useNavigate();
  const permissions = useClientPermissions();
  const [tab, setTab] = useState('alerts');
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/alerts`);
      setAlerts(res.data.alerts || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/alerts/preferences`);
      setPreferences(res.data);
    } catch (err) {
      console.error('Error fetching prefs:', err);
    }
  }, []);

  useEffect(() => { fetchAlerts(); fetchPreferences(); }, [fetchAlerts, fetchPreferences]);

  const scanAlerts = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/client/alerts/scan`);
      if (res.data.generated > 0) fetchAlerts();
    } catch (err) {
      console.error('Scan error:', err);
    } finally {
      setScanning(false);
    }
  };

  const markRead = async (alertId) => {
    await axios.post(`${API}/client/alerts/${alertId}/read`);
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, read: true } : a));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.post(`${API}/client/alerts/read-all`);
    setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    setUnreadCount(0);
  };

  const updatePref = async (key, value) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/client/alerts/preferences`, updated);
    } catch (err) {
      console.error('Error saving prefs:', err);
    } finally {
      setSavingPrefs(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr);
    const now = new Date();
    const diff = (now - dt) / 1000;
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `il y a ${Math.floor(diff / 86400)}j`;
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const visiblePrefTypes = PREF_TYPES.filter(p => !p.financial || permissions.canViewFinances);

  return (
    <div className="space-y-6" data-testid="client-alerts-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-bold text-gray-900">Mes alertes</h1>
          <p className="text-gray-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} alerte${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}` : 'Toutes les alertes sont lues'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={scanAlerts} disabled={scanning} data-testid="scan-alerts-btn">
            <RefreshCw className={cn("w-4 h-4 mr-1", scanning && "animate-spin")} />
            Actualiser
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn">
              <CheckCircle className="w-4 h-4 mr-1" />
              Tout marquer lu
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="alerts" className="flex items-center gap-2" data-testid="tab-alerts">
            <Bell className="w-4 h-4" />
            Alertes
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{unreadCount}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="tab-settings">
            <Settings className="w-4 h-4" />
            Préférences
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alerts">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : alerts.length === 0 ? (
            <Card>
              <CardContent>
                <EmptyState
                  icon={Bell}
                  title="Aucune alerte"
                  description="Tout est en ordre"
                />
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => {
                const Icon = ALERT_ICONS[alert.type] || Bell;
                return (
                  <Card
                    key={alert.id}
                    className={cn(
                      "border-l-4 transition-all cursor-pointer hover:shadow-md",
                      ALERT_COLORS[alert.severity] || ALERT_COLORS.info,
                      alert.read && "opacity-60"
                    )}
                    onClick={() => {
                      if (!alert.read) markRead(alert.id);
                      if (alert.operationId) navigate(`/operations/${alert.operationId}`);
                    }}
                    data-testid={`alert-${alert.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "p-2 rounded-lg mt-0.5",
                          alert.severity === 'high' ? 'bg-red-100' :
                          alert.severity === 'medium' ? 'bg-amber-100' : 'bg-emerald-100'
                        )}>
                          <Icon className={cn(
                            "w-4 h-4",
                            alert.severity === 'high' ? 'text-red-600' :
                            alert.severity === 'medium' ? 'text-amber-600' : 'text-emerald-600'
                          )} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={cn("text-sm font-semibold text-gray-900 truncate", !alert.read && "font-bold")}>
                              {alert.title}
                            </h3>
                            {!alert.read && <span className="w-2 h-2 rounded-full bg-[#1db584] flex-shrink-0" />}
                          </div>
                          <p className="text-sm text-gray-600">{alert.message}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="outline" className={cn("text-xs border", SEVERITY_BADGE[alert.severity])}>
                              {SEVERITY_LABELS[alert.severity]}
                            </Badge>
                            <span className="text-xs text-gray-400">{formatTime(alert.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings">
          <Card data-testid="alert-preferences">
            <CardHeader>
              <CardTitle className="text-lg">Préférences de notification</CardTitle>
              <p className="text-sm text-gray-500">Configurez comment vous souhaitez être notifié pour chaque type d'alerte</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {visiblePrefTypes.map((pref) => {
                const Icon = pref.icon;
                return (
                  <div key={pref.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-white border">
                        <Icon className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{pref.label}</p>
                        <p className="text-xs text-gray-500">{pref.desc}</p>
                      </div>
                    </div>
                    <Select
                      value={preferences[pref.key] || 'both'}
                      onValueChange={(v) => updatePref(pref.key, v)}
                    >
                      <SelectTrigger className="w-[160px]" data-testid={`pref-${pref.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PREF_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
              {savingPrefs && <p className="text-xs text-[#1db584]">Enregistrement...</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientAlerts;
